import { ReactNode, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import {
    FunctionField,
    ReferenceField,
    ShowBase,
    useDelete,
    useGetList,
    useNotify,
    useRedirect,
    useRefresh,
    useShowContext,
    useUpdate,
} from 'react-admin';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Briefcase,
    Building2,
    Edit3,
    ExternalLink,
    Mail,
    Phone,
    Plus,
    User,
    X,
} from 'lucide-react';

import { ActivityLog } from '../activity/ActivityLog';
import { Account, Contact } from '../types';
import { ContactEditDialog } from './ContactEditDialog';

export const ContactShow = () => (
    <ShowBase>
        <ContactShowContent />
    </ShowBase>
);

const ContactShowContent = () => {
    const { record, isPending } = useShowContext<Contact>();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [deleteOne] = useDelete();
    const [update] = useUpdate();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();

    if (isPending || !record) return null;

    const fullName =
        `${record.first_name || ''} ${record.last_name || ''}`.trim();
    const initials =
        `${(record.first_name || '')[0] || ''}${(record.last_name || '')[0] || ''}`.toUpperCase();
    const primaryEmail = record.email_jsonb?.[0]?.email;
    const primaryPhone = record.phone_jsonb?.[0]?.number;

    // Where the user came from (for back nav)
    const backPath = location.state?.from;

    return (
        <Box sx={{ mt: 2 }}>
            {/* Breadcrumb */}
            {record.account_id && (
                <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                    <ReferenceField
                        source="account_id"
                        reference="accounts"
                        link={false}
                    >
                        <FunctionField
                            render={(account: Account) => (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '0.8125rem',
                                        color: '#10b981',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/accounts/${record.account_id}/show`
                                        )
                                    }
                                >
                                    {account.name}
                                </Typography>
                            )}
                        />
                    </ReferenceField>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: '#94a3b8',
                            mx: 0.5,
                        }}
                    >
                        ·
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: '#64748b',
                        }}
                    >
                        {fullName}
                    </Typography>
                </Box>
            )}

            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                    sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: '#10b981',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#0f172a',
                        }}
                    >
                        {fullName}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        {record.title && (
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#64748b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Briefcase size={12} />
                                {record.title}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Action buttons */}
                <Box display="flex" alignItems="center" gap={1.5}>
                    {record.account_id && (
                        <Button
                            startIcon={<ArrowLeft size={16} />}
                            onClick={() => {
                                if (backPath) {
                                    navigate(backPath);
                                } else {
                                    navigate(
                                        `/accounts/${record.account_id}/show`
                                    );
                                }
                            }}
                            sx={{
                                textTransform: 'none',
                                color: '#475569',
                                fontWeight: 500,
                                fontSize: '0.8125rem',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                px: 2,
                                '&:hover': {
                                    backgroundColor: '#f1f5f9',
                                },
                            }}
                        >
                            <ReferenceField
                                source="account_id"
                                reference="accounts"
                                link={false}
                            >
                                <FunctionField
                                    render={(account: Account) => (
                                        <>Back to {account.name}</>
                                    )}
                                />
                            </ReferenceField>
                        </Button>
                    )}
                    <Button
                        startIcon={<Edit3 size={16} />}
                        onClick={() => setEditDialogOpen(true)}
                        sx={{
                            textTransform: 'none',
                            color: '#475569',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                            },
                        }}
                    >
                        Edit Contact
                    </Button>
                    <IconButton
                        size="small"
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{
                            color: '#ef4444',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#fef2f2',
                            },
                        }}
                    >
                        <X size={16} />
                    </IconButton>
                </Box>
            </Box>

            {/* Tabs */}
            <Tabs
                value={0}
                sx={{
                    mb: 2,
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#10b981',
                    },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#64748b',
                        '&.Mui-selected': {
                            color: '#10b981',
                        },
                    },
                }}
            >
                <Tab label="Details" />
            </Tabs>

            {/* Details Tab Content */}
            <DetailsTab
                record={record}
                fullName={fullName}
                primaryEmail={primaryEmail}
                primaryPhone={primaryPhone}
                onEdit={() => setEditDialogOpen(true)}
                onLinkAccount={() => {
                    if (record.account_id) {
                        notify(
                            'This contact is already linked to an account. A contact can only be linked to one account.',
                            { type: 'warning' }
                        );
                    } else {
                        setLinkDialogOpen(true);
                    }
                }}
                onUnlinkAccount={() => setUnlinkDialogOpen(true)}
            />

            {/* Edit Dialog */}
            <ContactEditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                contact={record}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '12px', maxWidth: 400 },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Delete Contact
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569' }}>
                        Are you sure you want to delete{' '}
                        <strong>{fullName}</strong>? This action cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{
                            color: '#64748b',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            deleteOne(
                                'contacts',
                                {
                                    id: record.id,
                                    previousData: record,
                                },
                                {
                                    onSuccess: () => {
                                        notify('Contact deleted', {
                                            type: 'info',
                                        });
                                        redirect('/contacts');
                                    },
                                    onError: () => {
                                        notify('Failed to delete contact', {
                                            type: 'error',
                                        });
                                    },
                                }
                            );
                            setDeleteDialogOpen(false);
                        }}
                        sx={{
                            backgroundColor: '#ef4444',
                            '&:hover': {
                                backgroundColor: '#dc2626',
                            },
                            textTransform: 'none',
                            borderRadius: '8px',
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Link Account Dialog */}
            <LinkAccountDialog
                open={linkDialogOpen}
                onClose={() => setLinkDialogOpen(false)}
                contact={record}
            />

            {/* Unlink Account Confirmation Dialog */}
            <Dialog
                open={unlinkDialogOpen}
                onClose={() => setUnlinkDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '12px', maxWidth: 400 },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Unlink Account
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569' }}>
                        Remove this contact from the linked account? The contact
                        will not be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setUnlinkDialogOpen(false)}
                        sx={{
                            color: '#64748b',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            update(
                                'contacts',
                                {
                                    id: record.id,
                                    data: { account_id: null },
                                    previousData: record,
                                },
                                {
                                    onSuccess: () => {
                                        notify('Account unlinked', {
                                            type: 'info',
                                        });
                                        refresh();
                                    },
                                    onError: () => {
                                        notify('Failed to unlink account', {
                                            type: 'error',
                                        });
                                    },
                                }
                            );
                            setUnlinkDialogOpen(false);
                        }}
                        sx={{
                            backgroundColor: '#ef4444',
                            '&:hover': {
                                backgroundColor: '#dc2626',
                            },
                            textTransform: 'none',
                            borderRadius: '8px',
                        }}
                    >
                        Unlink
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/* ─────────────── Link Account Dialog ─────────────── */

const LinkAccountDialog = ({
    open,
    onClose,
    contact,
}: {
    open: boolean;
    onClose: () => void;
    contact: Contact;
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();

    const { data: searchResults } = useGetList<Account>(
        'accounts',
        {
            filter: searchQuery ? { q: searchQuery } : { id: -1 },
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'name', order: 'ASC' },
        },
        { enabled: searchQuery.length > 0 }
    );

    const handleClose = () => {
        onClose();
        setSearchQuery('');
    };

    const handleLink = (accountId: number | string) => {
        update(
            'contacts',
            {
                id: contact.id,
                data: { account_id: accountId },
                previousData: contact,
            },
            {
                onSuccess: () => {
                    notify('Account linked', { type: 'info' });
                    refresh();
                    handleClose();
                },
                onError: () => {
                    notify('Failed to link account', {
                        type: 'error',
                    });
                },
            }
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    minWidth: 400,
                    maxHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
                Link Account
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: '#475569', mb: 2 }}>
                    Search for an account to link to this contact.
                </DialogContentText>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        px: 1.5,
                        py: 0.5,
                        mb: 1,
                    }}
                >
                    <Building2
                        size={16}
                        color="#94a3b8"
                        style={{ marginRight: 8 }}
                    />
                    <input
                        type="text"
                        placeholder="Search accounts by name..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{
                            border: 'none',
                            outline: 'none',
                            flex: 1,
                            fontSize: '0.875rem',
                            padding: '6px 0',
                            background: 'transparent',
                        }}
                    />
                </Box>
                {searchResults && searchResults.length > 0 ? (
                    <List dense sx={{ py: 0 }}>
                        {searchResults.map(account => (
                            <ListItem key={account.id} disablePadding>
                                <ListItemButton
                                    onClick={() => handleLink(account.id)}
                                    sx={{
                                        borderRadius: '6px',
                                        '&:hover': {
                                            backgroundColor: '#f0fdf4',
                                        },
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: '#ecfdf5',
                                            }}
                                        >
                                            <Building2
                                                size={16}
                                                color="#10b981"
                                            />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={account.name}
                                        secondary={account.sector || undefined}
                                        primaryTypographyProps={{
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: '0.75rem',
                                            color: '#64748b',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ) : searchQuery ? (
                    <Typography
                        sx={{
                            color: '#94a3b8',
                            fontSize: '0.875rem',
                            py: 2,
                            textAlign: 'center',
                        }}
                    >
                        No accounts found
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: '#64748b',
                        textTransform: 'none',
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/* ─────────────── Details Tab ─────────────── */

const DetailsTab = ({
    record,
    fullName,
    primaryEmail,
    primaryPhone,
    onEdit,
    onLinkAccount,
    onUnlinkAccount,
}: {
    record: Contact;
    fullName: string;
    primaryEmail?: string;
    primaryPhone?: string;
    onEdit: () => void;
    onLinkAccount: () => void;
    onUnlinkAccount: () => void;
}) => (
    <Box
        display="flex"
        gap={2}
        sx={{
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
        }}
    >
        {/* Left Column */}
        <Box
            sx={{
                flex: '0 0 55%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* Contact Information Card */}
            <Card
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: '#0f172a',
                        }}
                    >
                        Contact Information
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onEdit}
                        sx={{
                            color: '#64748b',
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                            },
                        }}
                    >
                        <Edit3 size={15} />
                    </IconButton>
                </Box>
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Stack spacing={2}>
                        <InfoRow
                            label="Full Name"
                            value={fullName}
                            icon={<User size={14} />}
                        />
                        {record.title && (
                            <InfoRow
                                label="Title"
                                value={record.title}
                                icon={<Briefcase size={14} />}
                            />
                        )}
                        <InfoRow
                            label="Phone Number"
                            value={primaryPhone || 'No phone number'}
                            icon={<Phone size={14} />}
                        />
                        <InfoRow
                            label="Email"
                            value={
                                primaryEmail ? (
                                    <Typography
                                        component="a"
                                        href={`mailto:${primaryEmail}`}
                                        sx={{
                                            fontSize: '0.8125rem',
                                            color: '#10b981',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {primaryEmail}
                                    </Typography>
                                ) : (
                                    'No email'
                                )
                            }
                            icon={<Mail size={14} />}
                        />
                    </Stack>
                </Box>
            </Card>

            {/* Related Accounts Card */}
            <Card
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: '#0f172a',
                        }}
                    >
                        Related Accounts ({record.account_id ? 1 : 0})
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onLinkAccount}
                        sx={{
                            color: '#10b981',
                            '&:hover': {
                                backgroundColor: '#f0fdf4',
                            },
                        }}
                    >
                        <Plus size={16} />
                    </IconButton>
                </Box>
                {record.account_id ? (
                    <ReferenceField
                        source="account_id"
                        reference="accounts"
                        link={false}
                    >
                        <RelatedAccountRow onUnlink={onUnlinkAccount} />
                    </ReferenceField>
                ) : (
                    <Box py={3} textAlign="center">
                        <Typography
                            sx={{
                                color: '#94a3b8',
                                fontSize: '0.875rem',
                            }}
                        >
                            No account linked
                        </Typography>
                    </Box>
                )}
            </Card>
        </Box>

        {/* Right Column — Recent Activity */}
        <Card
            sx={{
                flex: 1,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 520,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        color: '#0f172a',
                    }}
                >
                    Recent Activity
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 1,
                }}
            >
                {record.account_id ? (
                    <ActivityLog
                        accountId={record.account_id}
                        context="contact"
                        pageSize={10}
                    />
                ) : (
                    <Box py={4} textAlign="center">
                        <Typography
                            sx={{
                                color: '#94a3b8',
                                fontSize: '0.875rem',
                            }}
                        >
                            No account linked — activity appears here when the
                            contact is linked to an account.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Card>
    </Box>
);

/* ─────────────── Shared Components ─────────────── */

const InfoRow = ({
    label,
    value,
    icon,
}: {
    label: string;
    value?: string | ReactNode | null;
    icon?: ReactNode;
}) => (
    <Box display="flex" alignItems="flex-start" gap={1.5}>
        {icon && (
            <Box
                sx={{
                    color: '#94a3b8',
                    mt: '2px',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                }}
            >
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography
                    sx={{
                        fontSize: '0.8125rem',
                        color: value ? '#0f172a' : '#cbd5e1',
                        fontWeight: value ? 500 : 400,
                    }}
                >
                    {value || '—'}
                </Typography>
            ) : (
                value || (
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: '#cbd5e1',
                        }}
                    >
                        —
                    </Typography>
                )
            )}
        </Box>
    </Box>
);

const RelatedAccountRow = ({ onUnlink }: { onUnlink: () => void }) => {
    const location = useLocation();
    return (
        <FunctionField
            render={(account: Account) => (
                <List dense sx={{ py: 0 }}>
                    <ListItem
                        disablePadding
                        secondaryAction={
                            <IconButton
                                edge="end"
                                size="small"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onUnlink();
                                }}
                                sx={{
                                    color: '#ef4444',
                                    '&:hover': {
                                        backgroundColor: '#fef2f2',
                                    },
                                }}
                            >
                                <X size={14} />
                            </IconButton>
                        }
                    >
                        <ListItemButton
                            component={RouterLink}
                            to={`/accounts/${account.id}/show`}
                            state={{
                                from: location.pathname,
                            }}
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: '#f8fafc',
                                },
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        backgroundColor: '#ecfdf5',
                                    }}
                                >
                                    <Building2 size={18} color="#10b981" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={account.name}
                                secondary={account.sector || undefined}
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#0f172a',
                                }}
                                secondaryTypographyProps={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            )}
        />
    );
};
